import React, { useEffect, useState } from "react";
import { colors } from "styles/color-tokens";
import styled from "styled-components";
import { Icon } from "@dialogueconsulting/sked-ui";
import Bugsnag from "@bugsnag/js";
import { toError } from "utils/to-error";
import { UpsellFooter } from "./upsell-footer";
import { planEstimate, updatePlan } from "./plan-api";
import {
  PlanCurrency,
  PlanEstimateResponse,
  PlanPeriodUnit,
  SkedPlanCodeName,
} from "./types";
import { toast } from "react-toastify";
import { openSupportChat } from "libs/support";
import {
  GenericErrorMessage,
  renderPriceWithSymbol,
  UpsellHeader,
} from "./common-components";
import { Alert } from "ui";
import { Spinner } from "@/components/ui/spinner";
import { getPlanPeriodName } from "./plan-utils";
import { planBaseMaxAccounts } from "./constants/plans";
import {
  formatUnixBillingDate,
  formatUnixBillingDateCompact,
} from "../../utils/subscription-timing";
import sumBy from "lodash/sumBy";
import { User } from "features/user";
import { useTrackEvent } from "libs/analytics";
import {
  UPGRADE_CONFIRM_VIEWED,
  UPGRADE_FAILED,
  UPGRADE_SUCCESS,
} from "constants/Events";
import { AlertType } from "../../ui/alert";
import { AccountsCount } from "../../api/accounts";
import { useSelector } from "react-redux";
import { getAccountsCounts } from "../../selectors/skedCoreSelectors";
import { getUser } from "../../selectors/commonSelectors";
import { Button } from "@/components/ui/button";

// Kept above `buildConfirmButtonLabel` so the dependency is lexically
// obvious. `const` declarations have no function-style hoisting, so a
// forward reference happens to work only because the module is fully
// evaluated before any consumer calls the builder — a fragile invariant
// for a helper that's exported specifically to be called in isolation
// (tests, future callers).
const formatPrice = (price: number): string => {
  const scaled = price / 100;
  // Return a string sentinel for NaN rather than the NaN number — callers
  // (renderPriceWithSymbol, JSX text concatenation) expect a string and
  // would otherwise silently surface "NaN" text to users.
  if (Number.isNaN(scaled)) return "—";
  return Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(2);
};

interface ConfirmButtonLabelInput {
  isDowngrade: boolean;
  // Recurring price the user will be charged at every subsequent renewal.
  // Sourced from Chargebee `next_invoice_estimate.amount_due`, which is
  // the full next-period price and *excludes* any current-cycle proration.
  nextBillingAmount: number | null | undefined;
  // Prorated amount owed right now (mid-cycle upgrades, seat add-ons, etc).
  // Sourced from Chargebee `invoice_estimate.amount_due`. Surfaced as a
  // subline when positive so the CTA doesn't hide the immediate charge
  // behind the cheaper-looking recurring "$X/mo" number.
  amountDueToday: number | null | undefined;
  currency: PlanCurrency;
  periodUnit: PlanPeriodUnit;
}

const isPositiveAmount = (value: number | null | undefined): value is number =>
  typeof value === "number" && value > 0;

/**
 * Pure builder for the "Confirm change …" CTA label. Exported so the
 * renderer remains declarative (no IIFE inside JSX) and the label logic is
 * unit-testable in isolation.
 *
 * Emits a stacked label when both a recurring price and an immediate
 * prorated charge apply — the primary line reads "Confirm change ($X/mo)"
 * so users know the ongoing cost, and the subline reads "(pay $Y today)"
 * so they aren't surprised by a same-session charge.
 */
export const buildConfirmButtonLabel = ({
  isDowngrade,
  nextBillingAmount,
  amountDueToday,
  currency,
  periodUnit,
}: ConfirmButtonLabelInput): React.ReactNode => {
  if (isDowngrade) return "Downgrade at next billing period";

  const hasRecurring = isPositiveAmount(nextBillingAmount);
  const hasProrated = isPositiveAmount(amountDueToday);

  // Both estimates missing — we know nothing, so avoid surfacing any price.
  const estimateMissing =
    (nextBillingAmount === undefined || nextBillingAmount === null) &&
    (amountDueToday === undefined || amountDueToday === null);
  if (estimateMissing) return "Confirm change";

  // Both estimates present but zero (credits covered everything).
  if (!hasRecurring && !hasProrated) {
    return "Confirm change (no payment due today)";
  }

  const period = periodUnit === "year" ? "yr" : "mo";
  const primary = hasRecurring ? (
    <>
      {"Confirm change ("}
      {renderPriceWithSymbol({
        currency,
        price: formatPrice(nextBillingAmount),
      })}
      {`/${period})`}
    </>
  ) : (
    "Confirm change"
  );

  if (!hasProrated) return primary;

  const subline = (
    <span className="text-xs font-normal opacity-80">
      {"(pay "}
      {renderPriceWithSymbol({
        currency,
        price: formatPrice(amountDueToday),
      })}
      {" today)"}
    </span>
  );

  return (
    <span className="inline-flex flex-col items-center leading-tight">
      <span>{primary}</span>
      {subline}
    </span>
  );
};

interface OrderSummaryContext {
  periodUnit: PlanPeriodUnit;
  currency: PlanCurrency;
  planId: string;
  numberOfAccounts: number;
  planName: string | undefined;
  extraAccountPrice: number;
  planAccountLimit?: number;
  isDowngrade?: boolean;
}

export const UpsellOrderSummary = ({
  onBack,
  onSuccess,
  context,
}: {
  onBack?: () => void;
  onSuccess: (
    context: OrderSummaryContext,
    subscription: User["subscription"]
  ) => void;
  context: OrderSummaryContext;
}) => {
  const trackEvent = useTrackEvent();
  const [estimateResult, setEstimateResult] = useState<PlanEstimateResponse>();
  const counts: AccountsCount = useSelector(getAccountsCounts);
  const { isError, isProcessing, wrappedRequest } = useRequest(
    async () => {
      const rs = await planEstimate({
        billingPeriod: context.periodUnit,
        currency: context.currency,
        planId: context.planId,
        numberAccounts: context.numberOfAccounts,
        isDowngrade: context.isDowngrade || false,
      });
      setEstimateResult(rs);
    },
    {
      errorSupportMessage: "",
    }
  );
  useEffect(() => {
    wrappedRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (estimateResult) {
      trackEvent({
        eventName: UPGRADE_CONFIRM_VIEWED,
        useServices: ["AMP", "HS"],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateResult]);

  if (isProcessing) {
    return (
      <MainContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </MainContainer>
    );
  }

  if (isError || !estimateResult) {
    return (
      <MainContainer>
        <GenericErrorMessage />
      </MainContainer>
    );
  }

  const { next_invoice_estimate, subscription_estimate } =
    estimateResult.data.planEstimate.estimate;
  const { invoice_estimate, credit_note_estimates } =
    estimateResult.data.invoiceEstimate.estimate;
  const nextInvoiceEstimate = next_invoice_estimate;
  const planLabel =
    getPlanItem(estimateResult)?.description ||
    "Unknown plan - check with support";
  const baseNumberOfAccount = getBaseNumberOfAccount(
    context.planId as SkedPlanCodeName
  );
  return (
    <MainContainer>
      <GoBackContainer onClick={onBack} hidden={!onBack}>
        <Icon name="arrow-left" color={colors.primary500} size="md" />
        Go back
      </GoBackContainer>
      <Content>
        <BillingItemsDescriptionContainer>
          <ConfirmHeader>Confirm subscription change</ConfirmHeader>
          <PlanName>{planLabel} plan</PlanName>
          <InfoSection>
            <Paragraph>
              This plan has{" "}
              <Hl data-testid="base-account-number">{baseNumberOfAccount}</Hl>{" "}
              included accounts plus{" "}
              <Hl data-testid="extra-account-number">
                {getExtraNumberOfAccount(
                  context.planId as SkedPlanCodeName,
                  context.numberOfAccounts
                )}
              </Hl>{" "}
              addon accounts for a total of{" "}
              {baseNumberOfAccount > context.numberOfAccounts
                ? baseNumberOfAccount
                : context.numberOfAccounts}{" "}
              accounts you can add. You currently have{" "}
              {counts?.totalCount || "unknown"} accounts in Sked.
            </Paragraph>
          </InfoSection>
          <InfoSection>
            <Paragraph>
              You can add or remove more accounts whenever you need for{" "}
              <Hl>
                {renderPriceWithSymbol({
                  currency: context.currency,
                  periodUnit: context.periodUnit,
                  price: formatPrice(context.extraAccountPrice),
                })}
                /{context.periodUnit}
              </Hl>{" "}
              per additional account.
            </Paragraph>
          </InfoSection>
          <InfoSection>
            <div>
              <Paragraph>
                Next billing date:{" "}
                <Hl>
                  {formatUnixBillingDate(subscription_estimate.next_billing_at)}
                </Hl>
              </Paragraph>
            </div>
            <div>
              <Paragraph>
                Total at renewal:{" "}
                <Hl>
                  {nextInvoiceEstimate
                    ? renderPriceWithSymbol({
                        currency: context.currency,
                        price: formatPrice(nextInvoiceEstimate.total),
                      })
                    : "-"}
                </Hl>
              </Paragraph>
            </div>
          </InfoSection>
          {context.isDowngrade && (
            <Alert
              type={AlertType.info}
              title={`Your plan will change on ${formatUnixBillingDateCompact(
                subscription_estimate.next_billing_at
              )}`}
            >
              Your downgrade will take effect at the end of your current billing
              period. You&#39;ll keep having access to the features on your
              current plan until this date.
            </Alert>
          )}
          {next_invoice_estimate &&
            next_invoice_estimate.credits_applied > 0 && (
              <Alert type={AlertType.info}>
                Your next invoice will have some credits applied from your
                account. A total of{" "}
                {renderPriceWithSymbol({
                  currency: context.currency,
                  price: formatPrice(next_invoice_estimate.credits_applied),
                })}{" "}
                will be applied to the next invoice, leaving the amount due
                being{" "}
                {renderPriceWithSymbol({
                  currency: context.currency,
                  price: formatPrice(next_invoice_estimate.amount_due),
                })}
                .
              </Alert>
            )}
          {invoice_estimate && invoice_estimate.amount_due > 0 && (
            <InfoSection>
              <Alert type={AlertType.warning}>
                A pro-rated{" "}
                <strong>
                  charge of{" "}
                  {renderPriceWithSymbol({
                    currency: context.currency,
                    price: formatPrice(invoice_estimate.amount_due),
                  })}{" "}
                  will apply immediately
                </strong>{" "}
                for the changes to your current billing period.
              </Alert>
            </InfoSection>
          )}
          {credit_note_estimates &&
            credit_note_estimates.length > 0 &&
            credit_note_estimates[0].amount_available > 0 && (
              <InfoSection>
                <Alert type={AlertType.info}>
                  A credit note of{" "}
                  {renderPriceWithSymbol({
                    currency: context.currency,
                    price: formatPrice(
                      credit_note_estimates[0].amount_available
                    ),
                  })}{" "}
                  will be added to your account and taken off future invoices.
                  Note this amount is not refundable to your payment method.
                </Alert>
              </InfoSection>
            )}
        </BillingItemsDescriptionContainer>
        <BillingSummarySection
          onSuccess={onSuccess}
          plansContext={context}
          estimateResult={estimateResult}
        />
      </Content>
      <UpsellFooter hideViewAllPlan feature="unknown" helpChatTopic="upsell" />
    </MainContainer>
  );
};

function getBaseNumberOfAccount(planName: SkedPlanCodeName) {
  return planBaseMaxAccounts[planName] || 1;
}

function getExtraNumberOfAccount(
  planName: SkedPlanCodeName,
  numberOfAccounts: number
) {
  return Math.max(0, numberOfAccounts - getBaseNumberOfAccount(planName));
}

function getBasePrice(estimate: PlanEstimateResponse) {
  const nextInvoiceEstimate =
    estimate.data.planEstimate.estimate.next_invoice_estimate;
  const found = nextInvoiceEstimate?.line_items.find(
    (c) => c.entity_type === "plan_item_price"
  );
  if (!found) {
    return 0;
  }
  return found.amount - (found.tax_amount || 0);
}
function getPlanItem(estimate: PlanEstimateResponse) {
  const nextInvoiceEstimate =
    estimate.data.planEstimate.estimate.next_invoice_estimate;
  const found = nextInvoiceEstimate?.line_items.find(
    (c) => c.entity_type === "plan_item_price"
  );
  if (!found) {
    return null;
  }
  return found;
}

const ConfirmHeader = styled(UpsellHeader)`
  margin-bottom: 16px;
`;

const InfoSection = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const PlanName = styled.div`
  //styleName: Web App/Paragraph 1 Semi Bold;
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: ${colors.primary500};
  margin-bottom: 16px;
`;

const BillingItemsDescriptionContainer = styled.div`
  max-width: 600px;
  margin-right: 112px;
`;

const Content = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const ContactSupportText = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const BillingSummarySection = ({
  plansContext,
  onSuccess,
  estimateResult,
}: {
  onSuccess: (
    context: OrderSummaryContext,
    subscription: User["subscription"]
  ) => void;
  plansContext: OrderSummaryContext;
  estimateResult: PlanEstimateResponse;
}) => {
  const { next_invoice_estimate } = estimateResult.data.planEstimate.estimate;
  const nextInvoiceEstimate = next_invoice_estimate;
  const { invoice_estimate, credit_note_estimates } =
    estimateResult.data.invoiceEstimate.estimate;
  const trackEvent = useTrackEvent();
  const user = useSelector(getUser);

  const { wrappedRequest } = useRequest(
    async () => {
      const subscription = await updatePlan({
        billingPeriod: plansContext.periodUnit,
        currency: plansContext.currency,
        planId: plansContext.planId,
        numberAccounts: plansContext.numberOfAccounts,
        // isDowngrade: plansContext.isDowngrade,
      });
      onSuccess(plansContext, subscription);
      trackEvent({
        eventName: UPGRADE_SUCCESS,
        useServices: ["AMP", "HS"],
      });

      // Push upgrade conversion event to dataLayer for GTM.
      // Only fire on actual upgrades, not downgrades.
      // value = immediate prorated charge (incremental revenue, not full plan price).
      // transaction_id = Chargebee subscription ID, shared with server-side webhook
      // event for Google Ads deduplication.
      if (!plansContext.isDowngrade) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "upgrade",
          plan: plansContext.planId,
          value: (invoice_estimate?.amount_due ?? 0) / 100,
          currency: plansContext.currency,
          ...(user?.userId && { user_id: user.userId }),
          event_id: crypto.randomUUID(),
          transaction_id: subscription?.subscription?.id,
        });
      }
    },
    {
      errorSupportMessage: `I'd like to upgrade my plan to ${
        getPlanItem(estimateResult)?.description
      }, but I'm having trouble upgrading, can you please help me out?`,
      onError: () => {
        trackEvent({
          eventName: UPGRADE_FAILED,
          useServices: ["AMP", "HS"],
        });
      },
    }
  );

  const gstItem = nextInvoiceEstimate?.taxes.length ? (
    <BillingItemRow
      label={`GST (AU) (10%)`}
      value={renderPriceWithSymbol({
        currency: plansContext.currency,
        price: formatPrice(sumBy(nextInvoiceEstimate.taxes, "amount")),
      })}
    />
  ) : undefined;

  return (
    <BillingSummaryBox className="max-sm:w-full max-sm:max-w-[100vw] max-sm:rounded-xl max-sm:m-0">
      <BillingSummaryHeading>Your Subscription</BillingSummaryHeading>
      <BillingItemRow
        label="Plan"
        value={getPlanItem(estimateResult)?.description}
        valueStyle={{
          color: colors.primary500,
          fontWeight: 600,
        }}
      />
      <BillingItemRow
        label="Billing cycle"
        value={getPlanPeriodName(plansContext.periodUnit)}
      />
      <Separator />
      <BillingItemRow
        label="Base price"
        value={renderPriceWithSymbol({
          currency: plansContext.currency,
          price: formatPrice(getBasePrice(estimateResult)),
        })}
      />
      <BillingItemRow
        label="Extra accounts"
        value={
          <span>
            {getExtraNumberOfAccount(
              plansContext.planId as SkedPlanCodeName,
              plansContext.numberOfAccounts
            )}{" "}
            x{" "}
            {renderPriceWithSymbol({
              currency: plansContext.currency,
              price: formatPrice(plansContext.extraAccountPrice),
            })}
          </span>
        }
      />
      {gstItem}
      {nextInvoiceEstimate?.date && (
        <div>
          <Separator />
          <BillingItemRow
            label={`Renews on ${formatUnixBillingDateCompact(
              nextInvoiceEstimate.date
            )}`}
            value={renderPriceWithSymbol({
              currency: plansContext.currency,
              price: formatPrice(nextInvoiceEstimate.amount_due),
            })}
            isTotal={!invoice_estimate}
          />
        </div>
      )}
      {invoice_estimate && invoice_estimate.amount_due > 0 && (
        <div>
          <Separator />
          <BillingItemRow
            label={`Pro-rata amount due now`}
            value={renderPriceWithSymbol({
              currency: plansContext.currency,
              price: formatPrice(invoice_estimate.amount_due),
            })}
            isTotal
          />
        </div>
      )}
      {credit_note_estimates &&
        credit_note_estimates.length > 0 &&
        credit_note_estimates[0].amount_available > 0 && (
          <div>
            <Separator />
            <BillingItemRow
              label={`Pro-rata credit added`}
              value={renderPriceWithSymbol({
                currency: plansContext.currency,
                price: formatPrice(credit_note_estimates[0].amount_available),
              })}
              isTotal
            />
          </div>
        )}
      <ConfirmButtonContainer className="max-md:flex-col max-md:gap-3">
        <Button variant="blue-primary" size="lg" onClick={wrappedRequest}>
          {buildConfirmButtonLabel({
            // plansContext.isDowngrade is optional; default to false so the
            // CTA shows the upgrade label (with price) rather than the
            // downgrade label when the flag wasn't set by the caller.
            isDowngrade: plansContext.isDowngrade ?? false,
            // Recurring renewal price drives the primary "$X/mo" part of
            // the label; prorated immediate charge drives the "(pay $Y
            // today)" subline when positive.
            nextBillingAmount: next_invoice_estimate?.amount_due,
            amountDueToday: invoice_estimate?.amount_due,
            currency: plansContext.currency,
            periodUnit: plansContext.periodUnit,
          })}
          {!plansContext.isDowngrade && (
            <i className="fa-regular fa-rocket-launch"></i>
          )}
        </Button>
      </ConfirmButtonContainer>
    </BillingSummaryBox>
  );
};

const ToastError = ({
  errorSupportMessage,
  string,
}: {
  errorSupportMessage: string;
  string?: string;
}) => {
  if (string) {
    return <span>{string}</span>;
  }
  return (
    <span>
      Oops, we encountered an issue while processing your request. Please try
      again later, or{" "}
      <ContactSupportText
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          openSupportChat(errorSupportMessage, "upsell");
        }}
      >
        contact support
      </ContactSupportText>{" "}
      for immediate assistance.
    </span>
  );
};

const useRequest = (
  func: () => Promise<void>,
  options: {
    errorSupportMessage: string;
    onError?: () => void;
  }
) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);

  async function wrappedRequest() {
    setIsProcessing(true);
    try {
      await func();
    } catch (error) {
      console.error("Error processing request", error);
      console.dir(error);
      Bugsnag.notify(toError(error), (event) => {
        event.context = "Upsell: order summary request failed";
      });
      const { errorSupportMessage, onError } = options;
      // @ts-expect-error error body not defined in TS
      if (error?.response?.data.error) {
        toast.error(
          <ToastError
            // @ts-expect-error error body not defined in TS
            string={error.response.data.error}
            errorSupportMessage={errorSupportMessage}
          />
        );
      } else {
        toast.error(<ToastError errorSupportMessage={errorSupportMessage} />);
      }

      setIsError(true);
      onError?.();
    } finally {
      setIsProcessing(false);
    }
  }

  return { isProcessing, isError, wrappedRequest };
};

const ConfirmButtonContainer = styled.div`
  margin-top: 24px;
  display: flex;

  > button {
    flex: 1;
  }
`;

const Separator = styled.div`
  border-top: 1px solid ${colors.neutral100};
  height: 1px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const BillingItemRow = ({
  label,
  value,
  isTotal,
  valueStyle,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  isTotal?: boolean;
  valueStyle?: React.CSSProperties;
}) => {
  return (
    <BillingItemRowContainer strong={!!isTotal}>
      <BillingItemLabel>{label}</BillingItemLabel>
      <BillingItemValue style={valueStyle}>{value}</BillingItemValue>
    </BillingItemRowContainer>
  );
};

const BillingItemRowContainer = styled.div<{ strong: boolean }>`
  display: flex;
  align-items: baseline;
  //styleName: Web App/Paragraph 2 Regular;
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.neutral800};
  ${(p) => {
    return (
      p.strong &&
      `
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
      `
    );
  }}
`;
const BillingItemLabel = styled.div``;
const BillingItemValue = styled.div`
  flex: 1;
  text-align: right;
`;

const BillingSummaryBox = styled.div`
  width: 400px;
  min-height: 200px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.primary500};
`;

const BillingSummaryHeading = styled.div`
  font-family: "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: ${colors.primary500};
  margin-bottom: 24px;
  text-align: center;
`;

const Hl = styled.span`
  font-weight: 600;
`;

const Paragraph = styled.span`
  //styleName: Web App/Paragraph 1 Regular;
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${colors.neutral800};
`;

const MainContainer = styled.div`
  min-height: 330px;
`;
const GoBackContainer = styled.div<{ hidden?: boolean }>`
  //styleName: Text xs/Semibold;
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: ${colors.primary500};
  display: flex;
  align-items: center;
  margin-top: -16px;
  margin-bottom: 14px;
  cursor: pointer;

  > div {
    margin-right: 8px;
  }
  ${(p) => p.hidden && `visibility: hidden;`}
`;
